// AddressPremium.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaMapMarkerAlt,
  FaHome,
  FaBuilding,
  FaTag,
  FaTimes,
  FaLocationArrow,
  FaUser,
  FaCheckCircle,
  FaPhone,
  FaEnvelope,
} from "react-icons/fa";
import axios from "axios";
import "./Adressnew.css";

const AddressPremium = () => {
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [addressId, setAddressId] = useState(null);
  
  const [formData, setFormData] = useState({
    doorNumber: "",
    addressLine: "",
    landMark: "",
    pincode: "",
    state: "",
    city: "",
    category: "Home",
    latitude: null,
    longitude: null,
    address: "",
  });

  const categories = [
    { value: "Home", icon: FaHome, color: "#3498db", bg: "#e3f2fd" },
    { value: "Office", icon: FaBuilding, color: "#2ecc71", bg: "#e8f5e9" },
    { value: "Other", icon: FaTag, color: "#e74c3c", bg: "#fdeaea" },
  ];

  const token = localStorage.getItem("authToken");
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Detect mobile view
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load Google Maps API script
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDRcDLmtI8sk6pFB3w1o1N0RyPnRYO2HXQ&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => setMapLoaded(true);
      document.head.appendChild(script);
    } else {
      setMapLoaded(true);
    }
  }, []);

  // Fetch addresses
  const fetchAddresses = async () => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        console.error("User ID not found");
        return;
      }

      const res = await axios.get(
        `https://backend.maamaas.com/subscription/api/user/get/addresses/${userId}`,
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              }
            : {},
        }
      );

      if (res?.data) {
        setAddresses(res.data);
      } else {
        setAddresses([]);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error?.response || error.message);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  // Initialize map when modal opens and map is loaded
  useEffect(() => {
    if (isOpen && mapLoaded && window.google) {
      setTimeout(() => {
        initMapWithLocation();
      }, 100);
    }
  }, [isOpen, mapLoaded]);

  const initMapWithLocation = () => {
    const mapElement = document.getElementById("premium-address-map");
    if (!mapElement) return;

    const defaultLocation = { lat: 17.385044, lng: 78.486671 };

    mapRef.current = new window.google.maps.Map(mapElement, {
      center: defaultLocation,
      zoom: 14,
      styles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }],
        },
      ],
    });

    markerRef.current = new window.google.maps.Marker({
      position: defaultLocation,
      map: mapRef.current,
      draggable: true,
      animation: window.google.maps.Animation.DROP,
    });

    setLatitude(defaultLocation.lat);
    setLongitude(defaultLocation.lng);

    mapRef.current.addListener("click", (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      markerRef.current.setPosition({ lat, lng });
      setLatitude(lat);
      setLongitude(lng);
      reverseGeocode(lat, lng);
    });

    markerRef.current.addListener("dragend", (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setLatitude(lat);
      setLongitude(lng);
      reverseGeocode(lat, lng);
    });
  };

  const reverseGeocode = (lat, lng) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (res, status) => {
      if (status === "OK" && res[0]) {
        const getComponent = (type) => {
          const component = res[0].address_components.find((c) =>
            c.types.includes(type)
          );
          return component ? component.long_name : "";
        };

        setFormData((prev) => ({
          ...prev,
          address: res[0].formatted_address,
          city: getComponent("locality") || getComponent("sublocality") || prev.city,
          state: getComponent("administrative_area_level_1") || prev.state,
          pincode: getComponent("postal_code") || prev.pincode,
        }));
      }
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openModal = () => {
    setAddressId(null);
    setFormData({
      doorNumber: "",
      addressLine: "",
      landMark: "",
      pincode: "",
      state: "",
      city: "",
      category: "Home",
      latitude: null,
      longitude: null,
      address: "",
    });
    setLatitude(null);
    setLongitude(null);
    setIsOpen(true);
  };

  const closeModal = () => setIsOpen(false);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation not supported");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        setLatitude(lat);
        setLongitude(lng);

        if (mapRef.current && markerRef.current) {
          const position = { lat, lng };
          mapRef.current.setCenter(position);
          markerRef.current.setPosition(position);
          reverseGeocode(lat, lng);
        }
      },
      () => {
        alert("Unable to fetch your location");
      }
    );
  };

  const handleEdit = (address) => {
    setAddressId(address.addressId || address.id);
    setFormData({
      doorNumber: address.doorNumber || "",
      addressLine: address.addressLine || "",
      landMark: address.landMark || "",
      pincode: address.pincode || "",
      state: address.state || "",
      city: address.city || "",
      category: address.category || "Home",
      latitude: address.latitude || null,
      longitude: address.longitude || null,
      address: address.address || "",
    });
    setLatitude(address.latitude || null);
    setLongitude(address.longitude || null);
    setIsOpen(true);
  };

  const handleDelete = async (addressId) => {
    if (!addressId) {
      alert("❌ Invalid address ID.");
      return;
    }

    const confirmDelete = window.confirm("Are you sure you want to delete this address?");
    if (!confirmDelete) return;

    try {
      await axios.delete(
        `https://backend.maamaas.com/subscription/api/user/delete/${addressId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      alert("✅ Address deleted successfully!");
      fetchAddresses();
    } catch (error) {
      console.error("Error deleting address:", error.response || error.message);
      alert("❌ Failed to delete address.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const userId = localStorage.getItem("userId");

    if (latitude == null || longitude == null) {
      alert("Please select location on map to get latitude & longitude");
      setLoading(false);
      return;
    }

    const addressString =
      `doorNumber: ${formData.doorNumber || "null"}, ` +
      `addressLine: ${formData.addressLine}, ` +
      `landMark: ${formData.landMark}, ` +
      `city: ${formData.city}, ` +
      `state: ${formData.state}, ` +
      `pincode: ${Number(formData.pincode)}`;

    const body = {
      userId: Number(userId),
      doorNumber: formData.doorNumber || null,
      addressLine: formData.addressLine,
      landMark: formData.landMark,
      city: formData.city,
      state: formData.state,
      pincode: Number(formData.pincode),
      category: formData.category,
      latitude: Number(latitude),
      longitude: Number(longitude),
      address: addressString,
    };

    try {
      if (addressId) {
        await axios.put(
          `https://backend.maamaas.com/subscription/api/user/update/${addressId}`,
          body,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        alert("✅ Address updated successfully!");
      } else {
        await axios.post(
          `https://backend.maamaas.com/subscription/api/user/location/add`,
          body,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        alert("✅ Address added successfully!");
      }

      fetchAddresses();
      closeModal();
    } catch (error) {
      console.error("❌ Address save failed:", error);
      alert(error.response?.data?.message || "Failed to save address");
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const categoryObj = categories.find((cat) => cat.value === category);
    const IconComponent = categoryObj ? categoryObj.icon : FaTag;
    const color = categoryObj ? categoryObj.color : "#95a5a6";
    return <IconComponent style={{ color }} />;
  };

  const getCategoryStyle = (category) => {
    const categoryObj = categories.find((cat) => cat.value === category);
    return {
      background: categoryObj ? categoryObj.bg : "#f0f0f0",
      color: categoryObj ? categoryObj.color : "#666",
    };
  };

  return (
    <div className="premiumAddress-root">
      <div className="premiumAddress-container">
        {/* Header Section */}
        <div className="premiumAddress-header">
          <div className="premiumAddress-headerLeft">
            <div className="premiumAddress-headerIcon">
              <FaMapMarkerAlt />
            </div>
            <div className="premiumAddress-headerText">
              <h2 className="premiumAddress-title">My Addresses</h2>
              <p className="premiumAddress-subtitle">
                Manage your delivery locations
              </p>
            </div>
          </div>
          <button className="premiumAddress-addBtn" onClick={openModal}>
            <FaPlus className="premiumAddress-addIcon" />
            <span>Add New Address</span>
          </button>
        </div>

        {/* Desktop View - Premium Table Format */}
        {!isMobile && (
          <div className="premiumAddress-tableWrapper">
            {addresses.length === 0 ? (
              <div className="premiumAddress-empty">
                <FaMapMarkerAlt className="premiumAddress-emptyIcon" />
                <p>No addresses added yet</p>
                <button className="premiumAddress-emptyBtn" onClick={openModal}>
                  Add Your First Address
                </button>
              </div>
            ) : (
              <table className="premiumAddress-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Category</th>
                    <th>Door No</th>
                    <th>Address Line</th>
                    <th>Landmark</th>
                    <th>City</th>
                    <th>State</th>
                    <th>Pincode</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {addresses.map((addr, index) => (
                    <tr key={addr.addressId || addr.id}>
                      <td className="premiumAddress-sNo">{index + 1}</td>
                      <td>
                        <span
                          className="premiumAddress-categoryBadge"
                          style={getCategoryStyle(addr.category)}
                        >
                          {getCategoryIcon(addr.category)}
                          <span>{addr.category}</span>
                        </span>
                       </td>
                      <td className="premiumAddress-doorNo">{addr.doorNumber || "—"}</td>
                      <td className="premiumAddress-addressLine">{addr.addressLine || "—"}</td>
                      <td className="premiumAddress-landmark">{addr.landMark || "—"}</td>
                      <td className="premiumAddress-city">{addr.city || "—"}</td>
                      <td className="premiumAddress-state">{addr.state || "—"}</td>
                      <td className="premiumAddress-pincode">{addr.pincode || "—"}</td>
                      <td>
                        <div className="premiumAddress-tableActions">
                          <button
                            className="premiumAddress-tableEdit"
                            onClick={() => handleEdit(addr)}
                            title="Edit Address"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="premiumAddress-tableDelete"
                            onClick={() => handleDelete(addr.addressId || addr.id)}
                            title="Delete Address"
                          >
                            <FaTrash />
                          </button>
                        </div>
                       </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Mobile View - Card Format */}
        {isMobile && (
          <div className="premiumAddress-mobileGrid">
            {addresses.length === 0 ? (
              <div className="premiumAddress-empty">
                <FaMapMarkerAlt className="premiumAddress-emptyIcon" />
                <p>No addresses added yet</p>
                <button className="premiumAddress-emptyBtn" onClick={openModal}>
                  Add Your First Address
                </button>
              </div>
            ) : (
              addresses.map((addr, index) => (
                <div key={addr.addressId || addr.id} className="premiumAddress-mobileCard">
                  <div className="premiumAddress-mobileCardHeader">
                    <div className="premiumAddress-mobileSerial">Address #{index + 1}</div>
                    <span
                      className="premiumAddress-mobileCategory"
                      style={getCategoryStyle(addr.category)}
                    >
                      {getCategoryIcon(addr.category)}
                      <span>{addr.category}</span>
                    </span>
                  </div>
                  <div className="premiumAddress-mobileCardBody">
                    <div className="premiumAddress-mobileRow">
                      <span className="premiumAddress-mobileLabel">Door No:</span>
                      <span className="premiumAddress-mobileValue">{addr.doorNumber || "—"}</span>
                    </div>
                    <div className="premiumAddress-mobileRow">
                      <span className="premiumAddress-mobileLabel">Address:</span>
                      <span className="premiumAddress-mobileValue">{addr.addressLine || "—"}</span>
                    </div>
                    <div className="premiumAddress-mobileRow">
                      <span className="premiumAddress-mobileLabel">Landmark:</span>
                      <span className="premiumAddress-mobileValue">{addr.landMark || "—"}</span>
                    </div>
                    <div className="premiumAddress-mobileRow">
                      <span className="premiumAddress-mobileLabel">City:</span>
                      <span className="premiumAddress-mobileValue">{addr.city || "—"}</span>
                    </div>
                    <div className="premiumAddress-mobileRow">
                      <span className="premiumAddress-mobileLabel">State:</span>
                      <span className="premiumAddress-mobileValue">{addr.state || "—"}</span>
                    </div>
                    <div className="premiumAddress-mobileRow">
                      <span className="premiumAddress-mobileLabel">Pincode:</span>
                      <span className="premiumAddress-mobileValue premiumAddress-pincode">
                        {addr.pincode || "—"}
                      </span>
                    </div>
                  </div>
                  <div className="premiumAddress-mobileCardFooter">
                    <button
                      className="premiumAddress-mobileEdit"
                      onClick={() => handleEdit(addr)}
                    >
                      <FaEdit />
                      <span>Edit</span>
                    </button>
                    <button
                      className="premiumAddress-mobileDelete"
                      onClick={() => handleDelete(addr.addressId || addr.id)}
                    >
                      <FaTrash />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Modal Popup for Add/Edit Address */}
        {isOpen && (
          <div className="premiumAddress-modalOverlay" onClick={closeModal}>
            <div className="premiumAddress-modal" onClick={(e) => e.stopPropagation()}>
              <div className="premiumAddress-modalHeader">
                <h3>{addressId ? "Edit Address" : "Add New Address"}</h3>
                <button className="premiumAddress-modalClose" onClick={closeModal}>
                  <FaTimes />
                </button>
              </div>

              <div className="premiumAddress-modalBody">
                <button
                  type="button"
                  className="premiumAddress-locationBtn"
                  onClick={useMyLocation}
                >
                  <FaLocationArrow />
                  <span>Use My Current Location</span>
                </button>

                <div className="premiumAddress-mapWrapper">
                  <div id="premium-address-map" className="premiumAddress-map"></div>
                  {latitude && longitude && (
                    <div className="premiumAddress-coords">
                      📍 Selected Location: {latitude.toFixed(6)}°, {longitude.toFixed(6)}°
                    </div>
                  )}
                </div>

                <form onSubmit={handleSubmit} className="premiumAddress-form">
                  <div className="premiumAddress-formGrid">
                    <div className="premiumAddress-formGroup">
                      <input
                        type="text"
                        name="doorNumber"
                        placeholder="Door / House Number *"
                        value={formData.doorNumber}
                        onChange={handleChange}
                        required
                        className="premiumAddress-input"
                      />
                    </div>

                    <div className="premiumAddress-formGroup">
                      <input
                        type="text"
                        name="addressLine"
                        placeholder="Address Line *"
                        value={formData.addressLine}
                        onChange={handleChange}
                        required
                        className="premiumAddress-input"
                      />
                    </div>

                    <div className="premiumAddress-formGroup">
                      <input
                        type="text"
                        name="landMark"
                        placeholder="Landmark *"
                        value={formData.landMark}
                        onChange={handleChange}
                        required
                        className="premiumAddress-input"
                      />
                    </div>

                    <div className="premiumAddress-formGroup">
                      <input
                        type="text"
                        name="city"
                        placeholder="City *"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="premiumAddress-input"
                      />
                    </div>

                    <div className="premiumAddress-formGroup">
                      <input
                        type="text"
                        name="state"
                        placeholder="State *"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        className="premiumAddress-input"
                      />
                    </div>

                    <div className="premiumAddress-formGroup">
                      <input
                        type="number"
                        name="pincode"
                        placeholder="Pincode *"
                        value={formData.pincode}
                        onChange={handleChange}
                        required
                        className="premiumAddress-input"
                      />
                    </div>

                    <div className="premiumAddress-formGroup premiumAddress-fullWidth">
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        required
                        className="premiumAddress-select"
                      >
                        {categories.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.value}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="premiumAddress-submitBtn"
                    disabled={loading}
                  >
                    {loading ? (
                      <span>⏳ Saving...</span>
                    ) : addressId ? (
                      "✓ Update Address"
                    ) : (
                      "+ Add Address"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressPremium;